import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill, spring } from "remotion";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: inter } = loadInter("normal", { weights: ["600", "700", "800"], subsets: ["latin"] });

const BellLargeSVG: React.FC = () => (
  <svg width="48" height="53" viewBox="0 0 72 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="36" cy="6" r="5" fill="#B8860B" />
    <path d="M36 6C36 6 14 18 14 44V58H58V44C58 18 36 6 36 6Z" fill="#FFD700" stroke="#B8860B" strokeWidth="2" />
    <ellipse cx="36" cy="58" rx="12" ry="5" fill="#B8860B" />
    <circle cx="36" cy="66" r="6" fill="#8B6914" />
    <ellipse cx="26" cy="28" rx="4" ry="9" fill="rgba(255,255,255,0.3)" transform="rotate(-15 26 28)" />
    <ellipse cx="46" cy="22" rx="2" ry="5" fill="rgba(255,255,255,0.15)" transform="rotate(10 46 22)" />
  </svg>
);

const ThumbUpSVG: React.FC<{ color: string }> = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 64 64" fill="none">
    <path d="M20 28L28 8C30 8 34 9 34 16V24H52C54 24 56 26 55 30L50 52C49 54 47 55 45 55H20V28Z" fill={color} strokeWidth="2" strokeLinejoin="round" />
    <rect x="8" y="28" width="12" height="28" rx="3" fill={color} strokeWidth="2" />
  </svg>
);

export const DevaKathaLS2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bellY = spring({ frame, fps, config: { damping: 10, stiffness: 80 }, durationInFrames: Math.round(1.5 * fps) });
  const bellTranslateY = interpolate(bellY, [0, 1], [-80, 0]);

  const bellAngle = interpolate(
    frame,
    [Math.round(0.3 * fps), Math.round(0.6 * fps), Math.round(0.9 * fps), Math.round(1.2 * fps), Math.round(1.5 * fps), Math.round(1.8 * fps)],
    [0, 28, -20, 12, -6, 0],
    { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
  );

  const cardY = interpolate(frame, [Math.round(0.8 * fps), Math.round(1.4 * fps)], [27, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const cardOpacity = interpolate(frame, [Math.round(0.8 * fps), Math.round(1.3 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });

  const likeClicked = frame >= Math.round(1.8 * fps);
  const subClicked = frame >= Math.round(2.5 * fps);

  const likeScale = interpolate(frame,
    [Math.round(1.8 * fps), Math.round(1.87 * fps), Math.round(2.0 * fps)],
    [1, 0.85, 1.08], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });

  const subScale = interpolate(frame,
    [Math.round(2.5 * fps), Math.round(2.57 * fps), Math.round(2.7 * fps)],
    [1, 0.85, 1.08], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });

  const fadeOut = interpolate(frame, [Math.round(3.5 * fps), Math.round(4 * fps)], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: fadeOut }}>
      <div style={{ position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%) scale(1.5)", transformOrigin: "bottom center", display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
        <div style={{
          transform: `translateY(${bellTranslateY}px) rotate(${bellAngle}deg)`,
          transformOrigin: "top center",
          filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.6))",
          marginBottom: -7,
          zIndex: 2,
        }}>
          <BellLargeSVG />
        </div>

        <div style={{
          opacity: cardOpacity,
          transform: `translateY(${cardY}px)`,
          background: "rgba(15,15,15,0.85)",
          backdropFilter: "blur(10px)",
          borderRadius: 11,
          padding: "9px 13px",
          border: "1px solid rgba(255,215,0,0.4)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 7,
          minWidth: 160,
        }}>
          <span style={{ color: "#FFD700", fontSize: 9, fontWeight: 600, fontFamily: inter, letterSpacing: 1, textTransform: "uppercase" }}>
            Deva Katha
          </span>
          <div style={{ display: "flex", gap: 7, width: "100%" }}>
            <div style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              background: likeClicked ? "#1a73e8" : "rgba(255,255,255,0.12)",
              border: `1px solid ${likeClicked ? "#1a73e8" : "rgba(255,255,255,0.2)"}`,
              borderRadius: 13, padding: "6px 0",
              transform: `scale(${likeScale})`,
              boxShadow: likeClicked ? "0 4px 14px rgba(26,115,232,0.5)" : "none",
            }}>
              <ThumbUpSVG color={likeClicked ? "#fff" : "rgba(255,255,255,0.8)"} />
              <span style={{ color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: inter, whiteSpace: "nowrap" }}>{likeClicked ? "Liked!" : "Like"}</span>
            </div>
            <div style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
              background: subClicked ? "#606060" : "#FF0000",
              borderRadius: 13, padding: "6px 0",
              transform: `scale(${subScale})`,
              boxShadow: subClicked ? "none" : "0 4px 18px rgba(255,0,0,0.55)",
            }}>
              <span style={{ color: "#fff", fontSize: 10, fontWeight: 800, fontFamily: inter, whiteSpace: "nowrap" }}>{subClicked ? "Subscribed ✓" : "Subscribe"}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
